import React from "react";
import { useParams } from "react-router-dom";
import { Page } from "@iq/models";
import { addProductVariantAttributes, createVariantsForProduct, getProduct, uploadProductImage } from "../../services/productService";
import styles from "./variableProductPage.module.css";
import CreateVariantModal from "../../modals/CreateProduct/CreateVariantModal";
import CreateProductModal from "../../modals/CreateProduct/CreateProductModal";
import VariantModal from "../../modals/VariantModal/VariantModal";
import useIsSmallScreen from "../../hooks/useIsSmallScreen";
function renderAttributeValue(value) {
    if (value === null || value === undefined || value === "") {
        return "—";
    }

    if (typeof value === "string") {
        return value;
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value);
}

export default function VariableProductPage() {
    const isSmallScreen = useIsSmallScreen();
    const { id } = useParams();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [payload, setPayload] = React.useState(null);

    const [productName, setProductName] = React.useState(null);
    const [productDescription, setProductDescription] = React.useState(null);
    const [isEditingProduct, setIsEditingProduct] = React.useState(false);
    const [editInitialProduct, setEditInitialProduct] = React.useState(null);
    const [hasUnsavedProductChanges, setHasUnsavedProductChanges] = React.useState(false);
    const [isAddAttributesModalOpen, setIsAddAttributesModalOpen] = React.useState(false);
    const [modalAttributeNames, setModalAttributeNames] = React.useState([]);
    const [modalVariantEntries, setModalVariantEntries] = React.useState([]);
    const [deleteVariantModalOpen, setDeleteVariantModalOpen] = React.useState(false);
    const [variantToDelete, setVariantToDelete] = React.useState(null);
    function normalizeVariantMetadata(variant = {}) {
        const parseNumber = (value, fallback = 0) => {
            const parsedValue = Number(value);
            return Number.isFinite(parsedValue) ? parsedValue : fallback;
        };

        const parseInteger = (value, fallback = 0) => {
            const parsedValue = Number(value);
            return Number.isInteger(parsedValue) ? parsedValue : fallback;
        };
        const normalizedSku = String(variant?.SKU ?? variant?.sku ?? "").trim();
        const priceCents = parseNumber(variant?.Price ?? variant?.price, 0);
        const costCents = parseNumber(variant?.Cost ?? variant?.cost, 0);
        const weightValue = parseNumber(variant?.Weight ?? variant?.weight, 0);
        const stockQuantity = parseInteger(variant?.Stock ?? variant?.stock, 0);

        const normalized = {
            ...variant,
            sku: {
                value: normalizedSku,
                normalized: normalizedSku.toLowerCase(),
                display: normalizedSku || "N/A",
            },
            price: {
                cents: priceCents,
                currency: "USD",
                display: `$${(priceCents / 100).toFixed(2)}`,
            },
            cost: {
                cents: costCents,
                currency: "USD",
                display: `$${(costCents / 100).toFixed(2)}`,
            },
            weight: {
                value: weightValue,
                unit: "lb",
                display: `${weightValue} lb`,
            },
            stock: {
                quantity: stockQuantity,
                status: stockQuantity > 0 ? "in_stock" : "out_of_stock",
                display: String(stockQuantity),
            },
        };

        return normalized;
    }

    const fetchProductDetail = React.useCallback(async (isActive = true) => {
        setLoading(true);
        setError("");

        try {
            const productPayload = await getProduct(id);
            if (!isActive) {
                return;
            }

            setPayload(productPayload || null);
            setProductName(productPayload?.product?.name || "");
            setProductDescription(productPayload?.product?.description || "");
        } catch (fetchError) {
            if (!isActive) {
                return;
            }

            setError(fetchError?.message || "Failed to load product detail.");
            setPayload(null);
            setProductName("");
            setProductDescription("");
        } finally {
            if (isActive) {
                setLoading(false);
            }
        }
    }, [id]);
    React.useEffect(() => {
        let isActive = true;

        fetchProductDetail(isActive);

        return () => {
            isActive = false;
        };
    }, [id]);

    const product = payload?.product || null;
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const attributeGroups = new Map();
    (Array.isArray(product?.attributes) ? product.attributes : []).forEach((attribute) => {
        const name = String(attribute?.name || attribute?.Name || "Attribute").trim();
        const value = renderAttributeValue(attribute?.value ?? attribute?.Value);
        if (!attributeGroups.has(name)) {
            attributeGroups.set(name, new Set());
        }
        attributeGroups.get(name).add(value);
    });

    const handleClickVariant = (variant) => {
        const rawVariantId = variant?.id ?? variant?.ID ?? variant?.variant_id ?? variant?.variantId ?? null;

        setVariantToDelete({
            ...variant,
            id: rawVariantId,
            ID: rawVariantId,
            variant_id: rawVariantId,
            variantId: rawVariantId,
            product_id: Number(id),
            productId: Number(id),
        });
        setDeleteVariantModalOpen(true);
    }

    const prepareProductDraft = () => {
        const attributeNames = Array.isArray(product?.attributes)
            ? Array.from(new Set(
                product.attributes
                    .map((attribute) => String(attribute?.name || attribute?.Name || "").trim())
                    .filter(Boolean)
            ))
            : [];

        setProductName(product?.name || "");
        setProductDescription(product?.description || "");
        setModalAttributeNames(attributeNames);
        setModalVariantEntries(Array.isArray(variants) ? variants.map((variant) => ({ ...variant })) : []);
        setHasUnsavedProductChanges(false);
        return { name: product?.name || "", description: product?.description || "", attributes: attributeNames };
    };

    const handleOpenEditProduct = () => {
        setEditInitialProduct(prepareProductDraft());
        setIsEditingProduct(true);
    };

    const handleOpenCreateVariant = () => {
        prepareProductDraft();
        setIsAddAttributesModalOpen(true);
    };

    // Backend hook: persist { productId, name, description, attributes } before closing.
    const handleSaveProductChanges = async () => {
        throw new Error("Saving product details and attribute names is not connected yet. Your changes have not been saved.");
    };

    const handleContinueEditingProduct = async (changes) => {
        // Backend hook: persist changes here before opening the variant editor.
        setProductName(changes.name);
        setProductDescription(changes.description);
        setModalAttributeNames(changes.attributes);
        setHasUnsavedProductChanges(true);
        setIsAddAttributesModalOpen(true);
    };

    const handleModalAttributesChange = React.useCallback((nextNames) => {
        const normalizedNames = (Array.isArray(nextNames) ? nextNames : [])
            .map((name) => String(name || "").trim())
            .filter(Boolean);

        setModalAttributeNames((previousNames) => (
            previousNames.length === normalizedNames.length
                && previousNames.every((name, index) => name === normalizedNames[index])
                ? previousNames
                : normalizedNames
        ));
    }, []);

    const handleAddAttributesSave = async (nextVariants = []) => {
        const variantEntries = Array.isArray(nextVariants) ? nextVariants : [];
        console.log("🔵 handleAddAttributesSave called with variants:", variantEntries);
        
        const newVariants = variantEntries.filter((variant) => {
            const variantId = Number(variant?.id ?? variant?.ID ?? variant?.variant_id ?? variant?.variantId ?? 0);
            return variantId === 0 || !Number.isFinite(variantId);
        });

        const existingVariants = variantEntries.filter((variant) => {
            const variantId = Number(variant?.id ?? variant?.ID ?? variant?.variant_id ?? variant?.variantId ?? 0);
            return Number.isFinite(variantId) && variantId > 0;
        });

        console.log("🟢 New variants to create:", newVariants);
        console.log("🟡 Existing variants to update:", existingVariants);

        setError("");

        if (newVariants.length > 0) {
            try {
                console.log("📤 Calling createVariantsForProduct with productId:", id, "variants:", newVariants);
                const result = await createVariantsForProduct(id, newVariants);
                console.log("✅ createVariantsForProduct result:", result);

                const createdVariants = Array.isArray(result?.variants) ? result.variants : [];
                const createdVariantIdBySku = new Map(createdVariants.map((createdVariant) => [
                    String(createdVariant?.sku || createdVariant?.SKU || "").trim().toLowerCase(),
                    Number(createdVariant?.id || createdVariant?.ID || 0),
                ]));

                for (const variant of newVariants) {
                    const sku = String(variant?.SKU || variant?.sku || "").trim().toLowerCase();
                    const variantId = Number(createdVariantIdBySku.get(sku) || 0);
                    const uploadItems = Array.isArray(variant?.UploadImages)
                        ? variant.UploadImages.filter((item) => item?.file instanceof File)
                        : [];

                    if (variantId <= 0 || uploadItems.length === 0) {
                        continue;
                    }

                    for (const imageItem of uploadItems) {
                        await uploadProductImage(id, imageItem.file, {
                            variantId,
                            altText: String(imageItem?.altText || `Variant ${sku}`).trim(),
                        });
                    }
                }
            } catch (error) {
                console.error("❌ createVariantsForProduct error:", error);
                throw new Error(`Failed to create new variants: ${error?.message || "unknown error"}`);
            }
        }

        for (const variant of existingVariants) {
            const variantId = Number(variant?.id ?? variant?.ID ?? variant?.variant_id ?? variant?.variantId ?? 0);
            const attributes = Array.isArray(variant?.Attributes ?? variant?.attributes)
                ? (variant.Attributes ?? variant.attributes)
                : [];
            const validAttributes = attributes.filter((attribute) => (
                String(attribute?.Name || attribute?.name || "").trim()
                && String(attribute?.Value || attribute?.value || "").trim()
            ));

            if (validAttributes.length === 0) {
                continue;
            }

            await addProductVariantAttributes(id, variantId, validAttributes);
        }

        const refreshed = await getProduct(id);
        setPayload(refreshed || null);
        setProductName(refreshed?.product?.name || "");
        setProductDescription(refreshed?.product?.description || "");
        setIsAddAttributesModalOpen(false);
        setModalAttributeNames([]);
        setModalVariantEntries([]);
    };


    if (loading) {
        return null;
    }

    if (error) {
        return (
            <Page>
                <div className={styles.page}>
                    <p role="alert">{error}</p>
                </div>
            </Page>
        );
    }

    if (!product) {
        return (
            <Page>
                <div className={styles.page}>
                    <p>No product found.</p>
                </div>
            </Page>
        );
    }


    return (
        <Page>
            <div className={styles.page}>
                <div className={styles.overview}>
                    <section className={styles.details} aria-label="Product overview">
                        <div className={styles.heading}>
                            <h1>{product.name}</h1>
                            <button type="button" className={styles.secondaryButton} onClick={handleOpenEditProduct}>Edit Details</button>
                        </div>
                        <p className={styles.description}>{product.description || "No description added."}</p>
                    </section>
                    <section aria-labelledby="product-attributes-heading" className={styles.attributeSection}>
                        <div className={styles.sectionHeader}>
                            <div>
                                <h2 id="product-attributes-heading">Attributes</h2>
                                <p className={styles.helper}>Options that define this product's variants.</p>
                            </div>
                            <button type="button" className={styles.primaryButton} onClick={handleOpenEditProduct}>
                                Add Attributes
                            </button>
                        </div>
                        {attributeGroups.size > 0 ? (
                            Array.from(attributeGroups, ([name, values]) => (
                                <div key={name} className={styles.attributeRow}>
                                    <h3>{name}</h3>
                                    <div className={styles.attributeValues}>
                                        {Array.from(values, (value) => <span key={value} className={styles.attributeValue}>{value}</span>)}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className={styles.empty}>No attribute options yet.</p>
                        )}
                    </section>
                </div>
                <section className={styles.variants} aria-labelledby="product-variants-heading">
                    <div className={styles.sectionHeader}>
                        <div>
                            <div className={styles.headingTitleContainer}>
                                <h2 id="product-variants-heading">Variants</h2>
                                <span className={styles.variantCount}>{variants.length}</span>
                            </div>
                            <p className={styles.helper}>Select a variant to view or edit its details.</p>
                        </div>
                            <button type="button" className={styles.primaryButton} onClick={handleOpenCreateVariant}>
                                Create Variant
                            </button>
                    </div>
                    {hasUnsavedProductChanges && <p className={styles.helper} role="status">Product details and attribute names are draft-only until backend integration is connected.</p>}
                    <div className={styles.variantList}>
                        {variants.length > 0 && (
                            <div className={styles.variantHeader}>
                                {["SKU", "Price", "Cost", "Weight", "Stock"].map((label) => (
                                    <span className={styles.metricLabel} key={label}>{label}</span>
                                ))}
                            </div>
                        )}
                        {variants.map((variant, index) => {
                            const metadata = normalizeVariantMetadata(variant);
                            return (
                                <button
                                    type="button"
                                    key={variant.id ?? variant.ID ?? index}
                                    className={styles.variantRow}
                                    aria-label={`View variant ${metadata.sku.display}`}
                                    onClick={() => handleClickVariant(metadata)}
                                >
                                    <span className={styles.variantSku}>
                                        <span>{metadata.sku.display}</span>
                                    </span>
                                    {[
                                        ["Price", metadata.price.display],
                                        ["Cost", metadata.cost.display],
                                        ["Weight", metadata.weight.display],
                                        ["Stock", metadata.stock.display],
                                    ].map(([label, value]) => (
                                        <span key={label} className={styles.variantMetric}>
                                            <span>{value}</span>
                                        </span>
                                    ))}
                                </button>
                            );
                        })}
                        {variants.length === 0 && <p className={styles.empty}>No Variants Yet.</p>}
                    </div>
                </section>
            </div>
            {isEditingProduct && (
                <CreateProductModal
                    isOpen
                    isEditing
                    initialProduct={{ ...editInitialProduct, name: productName, description: productDescription }}
                    onClose={() => setIsEditingProduct(false)}
                    attributes={modalAttributeNames}
                    onAttributesChange={handleModalAttributesChange}
                    onProductNameChange={setProductName}
                    onProductDescriptionChange={setProductDescription}
                    onSaveChanges={handleSaveProductChanges}
                    onContinueEditing={handleContinueEditingProduct}
                />
            )}
            <CreateVariantModal
                isOpen={isAddAttributesModalOpen}
                onClose={() => setIsAddAttributesModalOpen(false)}
                onBack={() => {
                    setIsAddAttributesModalOpen(false);
                    if (hasUnsavedProductChanges) setIsEditingProduct(true);
                }}
                onSave={handleAddAttributesSave}
                productName={productName || product?.name || ""}
                productDescription={productDescription ?? product?.description ?? ""}
                attributes={modalAttributeNames}
                variants={modalVariantEntries}
                onAttributesChange={handleModalAttributesChange}
                onVariantsChange={setModalVariantEntries}
                animateIn={false}
                animateOut={true}
            />
            <VariantModal 
                isOpen={deleteVariantModalOpen}
                variant={variantToDelete}
                onClose={() => setDeleteVariantModalOpen(false)}
                onVariantUpdated={fetchProductDetail}
                tall={isSmallScreen}
            />
        </Page>
    );
}
